import { createClient } from '@supabase/supabase-js'

// Supabase configuration from environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://txgtimzbgvykrhsczycd.supabase.co'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'sb_publishable_yU3bmWsoB_tBUfIMkhJXKA_7voRPGAE'

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Database types generated from Supabase
export interface Database {
  public: {
    Tables: {
      users: {
        Row: {
          id: string
          email: string
          username: string
          display_name: string | null
          avatar_url: string | null
          banner_url: string | null
          bio: string | null
          role: 'USER' | 'MODERATOR' | 'ADMIN'
          plan: 'FREE' | 'BASIC' | 'PRO_MONTHLY' | 'PRO_ANNUAL' | 'PERMANENT'
          points: number
          xp: number
          level: number
          balance: number
          referral_code: string
          referred_by: string | null
          affiliate_earnings: number
          // Marketplace fields
          hourly_rate: number | null
          skills: string[] | null
          portfolio: string[] | null
          gradient: string | null
          theme: string | null
          accent: string | null
          notification_preferences: Record<string, boolean> | null
          paypal_subscription_id: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          email: string
          username: string
          display_name?: string | null
          avatar_url?: string | null
          banner_url?: string | null
          bio?: string | null
          role?: 'USER' | 'MODERATOR' | 'ADMIN'
          plan?: 'FREE' | 'BASIC' | 'PRO_MONTHLY' | 'PRO_ANNUAL' | 'PERMANENT'
          points?: number
          xp?: number
          level?: number
          balance?: number
          referral_code?: string
          referred_by?: string | null
          affiliate_earnings?: number
          hourly_rate?: number | null
          skills?: string[] | null
          portfolio?: string[] | null
          gradient?: string | null
          theme?: string | null
          accent?: string | null
          notification_preferences?: Record<string, boolean> | null
          paypal_subscription_id?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          email?: string
          username?: string
          display_name?: string | null
          avatar_url?: string | null
          banner_url?: string | null
          bio?: string | null
          role?: 'USER' | 'MODERATOR' | 'ADMIN'
          plan?: 'FREE' | 'BASIC' | 'PRO_MONTHLY' | 'PRO_ANNUAL' | 'PERMANENT'
          points?: number
          xp?: number
          level?: number
          balance?: number
          referral_code?: string
          referred_by?: string | null
          affiliate_earnings?: number
          hourly_rate?: number | null
          skills?: string[] | null
          portfolio?: string[] | null
          gradient?: string | null
          theme?: string | null
          accent?: string | null
          notification_preferences?: Record<string, boolean> | null
          paypal_subscription_id?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      user_progress: {
        Row: {
          id: string
          user_id: string
          class_id: string
          progress: number
          completed: boolean
          completed_at: string | null
          last_watched_at: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          class_id: string
          progress?: number
          completed?: boolean
          completed_at?: string | null
          last_watched_at?: string
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          class_id?: string
          progress?: number
          completed?: boolean
          completed_at?: string | null
          last_watched_at?: string
          created_at?: string
          updated_at?: string
        }
      }
      user_missions: {
        Row: {
          id: string
          user_id: string
          mission_id: string
          progress: number
          completed: boolean
          xp_claimed: boolean
          completed_at: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          mission_id: string
          progress?: number
          completed?: boolean
          xp_claimed?: boolean
          completed_at?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          mission_id?: string
          progress?: number
          completed?: boolean
          xp_claimed?: boolean
          completed_at?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      missions: {
        Row: {
          id: string
          title: string
          description: string
          type: 'COMPLETE_CLASS' | 'PUBLISH_CONTENT' | 'INVITE_USER' | 'SELL_SERVICE' | 'FIRST_LOGIN' | 'COMPLETE_PROFILE'
          target: number
          xp_reward: number
          badge_reward: string | null
          is_active: boolean
          created_at: string
        }
        Insert: {
          id?: string
          title: string
          description: string
          type: 'COMPLETE_CLASS' | 'PUBLISH_CONTENT' | 'INVITE_USER' | 'SELL_SERVICE' | 'FIRST_LOGIN' | 'COMPLETE_PROFILE'
          target: number
          xp_reward: number
          badge_reward?: string | null
          is_active?: boolean
          created_at?: string
        }
        Update: {
          id?: string
          title?: string
          description?: string
          type?: 'COMPLETE_CLASS' | 'PUBLISH_CONTENT' | 'INVITE_USER' | 'SELL_SERVICE' | 'FIRST_LOGIN' | 'COMPLETE_PROFILE'
          target?: number
          xp_reward?: number
          badge_reward?: string | null
          is_active?: boolean
          created_at?: string
        }
      }
      classes: {
        Row: {
          id: string
          title: string
          slug: string
          description: string | null
          video_url: string | null
          duration: number | null
          order: number
          category_id: string
          xp_reward: number
          is_premium: boolean
          created_at: string
        }
        Insert: {
          id?: string
          title: string
          slug: string
          description?: string | null
          video_url?: string | null
          duration?: number | null
          order?: number
          category_id: string
          xp_reward?: number
          is_premium?: boolean
          created_at?: string
        }
        Update: {
          id?: string
          title?: string
          slug?: string
          description?: string | null
          video_url?: string | null
          duration?: number | null
          order?: number
          category_id?: string
          xp_reward?: number
          is_premium?: boolean
          created_at?: string
        }
      }
      categories: {
        Row: {
          id: string
          name: string
          slug: string
          description: string | null
          icon: string | null
          order: number
          created_at: string
        }
        Insert: {
          id?: string
          name: string
          slug: string
          description?: string | null
          icon?: string | null
          order?: number
          created_at?: string
        }
        Update: {
          id?: string
          name?: string
          slug?: string
          description?: string | null
          icon?: string | null
          order?: number
          created_at?: string
        }
      }
      chat_messages: {
        Row: {
          id: string
          user_id: string
          content: string
          created_at: string
        }
        Insert: {
          id?: string
          user_id: string
          content: string
          created_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          content?: string
          created_at?: string
        }
      }
      chat_cooldowns: {
        Row: {
          id: string
          user_id: string
          last_message_at: string
          created_at: string
        }
        Insert: {
          id?: string
          user_id: string
          last_message_at: string
          created_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          last_message_at?: string
          created_at?: string
        }
      }
      user_chat_profiles: {
        Row: {
          user_id: string
          chat_public_id: string
          created_at: string
        }
        Insert: {
          user_id: string
          chat_public_id: string
          created_at?: string
        }
        Update: {
          user_id?: string
          chat_public_id?: string
          created_at?: string
        }
      }
      private_threads: {
        Row: {
          id: string
          pair_key: string
          participant_one_id: string
          participant_two_id: string
          last_message_preview: string | null
          updated_at: string
          created_at: string
        }
        Insert: {
          id?: string
          pair_key: string
          participant_one_id: string
          participant_two_id: string
          last_message_preview?: string | null
          updated_at?: string
          created_at?: string
        }
        Update: {
          id?: string
          pair_key?: string
          participant_one_id?: string
          participant_two_id?: string
          last_message_preview?: string | null
          updated_at?: string
          created_at?: string
        }
      }
      private_messages: {
        Row: {
          id: string
          thread_id: string
          sender_id: string
          message_type: 'text' | 'image' | 'audio' | 'deal'
          content: string | null
          media_url: string | null
          deal_title: string | null
          deal_amount: number | null
          deal_payment_method: 'paypal' | 'visa' | null
          created_at: string
        }
        Insert: {
          id?: string
          thread_id: string
          sender_id: string
          message_type: 'text' | 'image' | 'audio' | 'deal'
          content?: string | null
          media_url?: string | null
          deal_title?: string | null
          deal_amount?: number | null
          deal_payment_method?: 'paypal' | 'visa' | null
          created_at?: string
        }
        Update: {
          id?: string
          thread_id?: string
          sender_id?: string
          message_type?: 'text' | 'image' | 'audio' | 'deal'
          content?: string | null
          media_url?: string | null
          deal_title?: string | null
          deal_amount?: number | null
          deal_payment_method?: 'paypal' | 'visa' | null
          created_at?: string
        }
      }
    }
  }
}

export type User = Database['public']['Tables']['users']['Row']
export type UserProgress = Database['public']['Tables']['user_progress']['Row']
export type UserMission = Database['public']['Tables']['user_missions']['Row']
export type Mission = Database['public']['Tables']['missions']['Row']
export type Class = Database['public']['Tables']['classes']['Row']
export type Category = Database['public']['Tables']['categories']['Row']
